// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useState } from "react"
import clsx from "clsx"
import FitnessCharts from "../FitnessCharts"
import CostCharts from "../CostCharts"
import AgentCostChart from "../AgentCostChart"

// Keep in sync with ebiose-webapp/src/views/cycles/details.tsx §CycleGraphs.
export interface CycleGraphsProps {
  cycleId: string
}

const TABS = [
  { id: "fitness", label: "Fitness" },
  { id: "cost", label: "Cost Breakdown" },
  { id: "agentCost", label: "Agent Evaluation Cost" },
] as const

type TabId = (typeof TABS)[number]["id"]

export default function CycleGraphs({ cycleId }: CycleGraphsProps) {
  const [activeTab, setActiveTab] = useState<TabId>("fitness")

  return (
    <section className="box flex flex-4 flex-col gap-4 p-5">
      <div className="flex flex-row flex-wrap items-center justify-between gap-3">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-text-tertiary">
          Cycle Graphs
        </span>
        <div
          role="tablist"
          className="flex gap-1 rounded-[10px] border border-border bg-surface-strong p-[3px]"
        >
          {TABS.map((tab) => (
            <div
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              tabIndex={0}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  setActiveTab(tab.id)
                }
              }}
              className={clsx(
                "cursor-pointer select-none rounded-[7px] px-3.5 py-1.5 text-[12.5px] transition-colors",
                activeTab === tab.id
                  ? "bg-primary/14 text-primary"
                  : "text-text-secondary hover:text-foreground",
              )}
            >
              {tab.label}
            </div>
          ))}
        </div>
      </div>
      <div className="w-full">
        {activeTab === "fitness" && <FitnessCharts cycleId={cycleId} />}
        {activeTab === "cost" && <CostCharts cycleId={cycleId} />}
        {activeTab === "agentCost" && <AgentCostChart cycleId={cycleId} />}
      </div>
    </section>
  )
}
